import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/axios';
import type { AxiosError } from 'axios';

interface User {
  id: string;
  email: string;
  role: string;
  exp: number;
  [key: string]: unknown;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginUser: (credentials: LoginCredentials) => void;
  loginMutation: {
    isPending: boolean;
    error: AxiosError | null;
  };
  logout: () => void;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isSeller: boolean;
  warehouse: number;
  setWarehouse: (warehouse: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

const decodeJwt = (token: string) => {
  try {
    const [headerEncoded, payloadEncoded] = token.split('.').slice(0, 2);

    const header = JSON.parse(
      atob(headerEncoded.replace(/-/g, '+').replace(/_/g, '/'))
    );
    const payload = JSON.parse(
      atob(payloadEncoded.replace(/-/g, '+').replace(/_/g, '/'))
    );

    return {
      header,
      payload: {
        id: payload.sub,
        email: payload.unique_name,
        firstName: payload.name,
        lastName: payload.family_name,
        role: payload.Role,
        exp: payload.exp,
      },
    };
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.payload.exp) return true;

  const currentTime = Date.now() / 1000;
  return decoded.payload.exp < currentTime;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [warehouse, setWarehouse] = useState<number>(0);
  const queryClient = useQueryClient();
  const JWT_TOKEN_KEY = 'jwtToken';

  useEffect(() => {
    const initializeAuth = () => {
      api.defaults.headers.common['x-warehouse'] = warehouse;

      try {
        const token = localStorage.getItem(JWT_TOKEN_KEY);

        if (token && !isTokenExpired(token)) {
          const decoded = decodeJwt(token);
          if (decoded && decoded.payload) {
            setUser(decoded.payload);
          }
        } else if (token) {
          // Token is expired, remove it
          localStorage.removeItem(JWT_TOKEN_KEY);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (isBrowser()) {
          localStorage.removeItem(JWT_TOKEN_KEY);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    try {
      api.defaults.headers.common['x-warehouse'] = warehouse;
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Error initializing warehouse:', error);
    }
  }, [warehouse]);

  const setAuthToken = (token: string) => {
    try {
      if (isTokenExpired(token)) {
        throw new Error('Token is expired');
      }

      if (isBrowser()) {
        localStorage.setItem(JWT_TOKEN_KEY, token);
      }
      const decoded = decodeJwt(token);

      if (decoded && decoded.payload) {
        setUser(decoded.payload);
      }
    } catch (error) {
      console.error('Error during token setting:', error);
      throw error;
    }
  };

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => {
      return api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });
    },
    onSuccess: ({ data }) => {
      const token = data.data.token;

      if (token) {
        setAuthToken(token);
      } else {
        throw new Error('No access token received');
      }
    },
    onError: (error: AxiosError) => {
      console.error('Login error:', error);
    },
  });

  const loginUser = (credentials: LoginCredentials) => {
    loginMutation.mutate(credentials);
  };

  const logout = () => {
    if (isBrowser()) {
      localStorage.removeItem(JWT_TOKEN_KEY);
    }
    setUser(null);
    queryClient.clear();
  };

  const isSuperAdmin = useMemo(() => user?.role === 'SuperAdmin', [user]);
  const isAdmin = useMemo(() => user?.role === 'Administrativo', [user]);
  const isSeller = useMemo(() => user?.role === 'Preventista', [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    loginUser,
    isSuperAdmin,
    isAdmin,
    isSeller,
    warehouse,
    setWarehouse,
    loginMutation: {
      isPending: loginMutation.isPending,
      error: loginMutation.error,
    },
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
